import React, { useCallback, useEffect, useMemo } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { AnyAction, combineReducers, createStore } from "redux";
import { getPopularCharacters, getCharacters } from "../../../api/rest-apis";
import {
  createCampaign as createCampaignApi,
  duplicateCampaign as duplicateCampaignApi,
  activateCampaign as activateCampaignApi,
  deactivateCampaign as deactivateCampaignApi,
  deleteCampaign as deleteCampaignApi,
  addReengagementSequenceStep,
  updateReengagementSequenceStep,
  deleteReengagementSequenceStep,
  fetchCampaignById,
  fetchCampaignSummaries,
  updateReengagementCharacterSelection,
  updateCharacterReplyTiming,
  updateNoChatSchedule,
  addNoChatScenario,
  updateNoChatScenario,
  deleteNoChatScenario,
  addReachOutRule,
  updateReachOutRule,
  deleteReachOutRule,
  type ReengagementSequenceStepApi,
  type NoChatIntroScenarioApi,
  type ReachOutCharacterRuleApi,
  type NoChatIntroScheduleRequest,
  type NoChatIntroScenarioRequest,
  type ReachOutCharacterRuleRequest,
  type CharacterStrategyApi,
} from "../../../api/campaigns";
import type { Character } from "../../../interfaces/rest-api";
import type {
  CampaignCharacter,
  EmailCampaignState,
  ManagedEmailCampaign,
  SequenceStep,
  SendSchedule,
  NoChatScenario,
  PremiumCharacterRule,
} from "./types";
import { DEFAULT_SEND_TIME_VALUE } from "./types";
import { mapCampaignApiToManaged } from "./helpers/campaignMappers";
import { cloneCampaignState } from "./utils/state";

type LoadStatus = "idle" | "loading" | "succeeded" | "failed";

interface CharacterLibraryState {
  items: CampaignCharacter[];
  status: LoadStatus;
  error?: string;
}

interface CampaignsState {
  items: ManagedEmailCampaign[];
  status: LoadStatus;
  error?: string;
  activeCampaignId?: string;
  selectedCampaignId?: string;
}

interface RootState {
  characterLibrary: CharacterLibraryState;
  campaigns: CampaignsState;
}

const resolveCharacterTier = (character: Character): CampaignCharacter["tier"] => {
  // Only honor the explicit premium flag; otherwise treat as free to avoid mislabeling.
  return character.includes_premium ? "premium" : "free";
};

const mapToCampaignCharacter = (character: Character): CampaignCharacter => ({
  id: character._id ?? character.character_name,
  name: character.character_name,
  tier: resolveCharacterTier(character),
});

const extractCharacterPayload = (response: any): Character[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.characters)) return response.characters;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const mapSequenceStepToRequestBody = (
  step: SequenceStep
) => ({
  step_name: step.label,
  offsetValue: step.offsetValue,
  offsetUnit: step.offsetUnit,
  timeToSend: step.sendTimeOptionId || undefined,
  prompt: step.prompt,
});

const hasSequenceStepChanged = (
  remote: ReengagementSequenceStepApi,
  local: SequenceStep
): boolean => {
  if (remote.step_name !== local.label) return true;
  if (remote.offsetValue !== local.offsetValue) return true;
  if (remote.offsetUnit !== local.offsetUnit) return true;
  const remoteTime = remote.timeToSend ?? DEFAULT_SEND_TIME_VALUE;
  const localTime = local.sendTimeOptionId ?? DEFAULT_SEND_TIME_VALUE;
  if (remoteTime !== localTime) return true;
  if (remote.prompt !== local.prompt) return true;
  return false;
};

const mapCharacterStrategyToApi = (
  strategy: EmailCampaignState["settings"]["characterStrategy"]
): CharacterStrategyApi => {
  switch (strategy) {
    case "most-frequent":
      return "character-with-most-conversation";
    case "last-high-token":
    default:
      return "last-character";
  }
};

const mapSendScheduleToApi = (
  schedule?: SendSchedule
): NoChatIntroScheduleRequest | undefined => {
  if (!schedule) return undefined;
  return {
    offsetValue: schedule.offsetValue,
    offsetUnit: schedule.offsetUnit,
    timeToSend: schedule.sendTimeOptionId || undefined,
  };
};

  const mapNoChatScenarioToRequest = (
    scenario: NoChatScenario
  ): NoChatIntroScenarioRequest => {
  const promptHasContent = Boolean(scenario.promptInstructions?.trim());
  const manualEmailEnabled =
    scenario.manualEmailEnabled !== undefined
      ? scenario.manualEmailEnabled
      : !promptHasContent;
  const manualEmailSubject = scenario.manualEmailSubject ?? scenario.subject;
  const manualEmailBody = scenario.manualEmailBody ?? scenario.body;

  return {
    storylineTitle: scenario.storylineTitle,
    storylineSummary: scenario.storylineSummary,
    characterId: scenario.characterId,
    from_character_id: scenario.from_character_id || scenario.characterId,
    subject: manualEmailSubject,
    body: manualEmailBody,
    prompts: scenario.prompts
      .map((prompt) => prompt.label?.trim() || prompt.id?.trim() || "")
      .filter(Boolean),
    promptInstructions: scenario.promptInstructions?.trim() || undefined,
    manualEmailEnabled,
    manualEmailSubject: manualEmailEnabled ? manualEmailSubject : undefined,
    manualEmailBody: manualEmailEnabled ? manualEmailBody : undefined,
    maxConversationCount: scenario.maxConversationCount,
  };
};

  const hasNoChatScenarioChanged = (
    remote: NoChatIntroScenarioApi,
    local: NoChatScenario
  ): boolean => {
  const remoteManualEmailEnabled =
    remote.manualEmailEnabled ?? !remote.promptInstructions;
  const localManualEmailEnabled =
    local.manualEmailEnabled ?? !local.promptInstructions;
  if (remoteManualEmailEnabled !== localManualEmailEnabled) return true;
  if (remote.storylineTitle !== local.storylineTitle) return true;
  if ((remote.storylineSummary ?? "") !== (local.storylineSummary ?? "")) return true;
  if (remote.characterId !== local.characterId) return true;
  const remoteFromCharacterId =
    (remote as any).from_character_id ?? remote.fromCharacterId ?? remote.characterId;
  if ((remoteFromCharacterId ?? remote.characterId) !== (local.from_character_id ?? local.characterId)) return true;
  const remoteSubject =
    remote.manualEmailSubject ?? remote.subject ?? "";
  const localSubject =
    local.manualEmailSubject ?? local.subject ?? "";
  if (remoteSubject !== localSubject) return true;
  const remoteBody =
    remote.manualEmailBody ?? remote.body ?? "";
  const localBody =
    local.manualEmailBody ?? local.body ?? "";
  if (remoteBody !== localBody) return true;
  if ((remote.promptInstructions ?? "") !== (local.promptInstructions ?? ""))
    return true;
  if (remote.maxConversationCount !== local.maxConversationCount) return true;
  const remotePrompts = (remote.prompts ?? []).map((prompt) => prompt?.trim() ?? "");
  const localPrompts = local.prompts
    .map((prompt) => prompt.label?.trim() || prompt.id?.trim() || "")
    .filter(Boolean);
  if (remotePrompts.length !== localPrompts.length) return true;
  for (let index = 0; index < remotePrompts.length; index += 1) {
    if (remotePrompts[index] !== localPrompts[index]) return true;
  }
  return false;
};

const mapReachOutRuleToRequest = (
  rule: PremiumCharacterRule
): ReachOutCharacterRuleRequest => ({
  id: rule.id,
  reachoutCharacterId: rule.reachoutCharacterId,
  reachoutCharacterTier: rule.reachoutCharacterTier?.trim()
    ? rule.reachoutCharacterTier
    : undefined,
  triggerCharacterIds: rule.triggerCharacterIds.length
    ? rule.triggerCharacterIds
    : rule.triggerAfterAnyCount !== undefined
      ? []
      : undefined,
  minimumTokens: rule.minimumTokens,
  triggerAfterAnyCount:
    typeof rule.triggerAfterAnyCount === "number"
      ? rule.triggerAfterAnyCount
      : undefined,
  resendCooldownDays: rule.resendCooldownDays,
  sendWindow: {
    offsetValue: rule.sendWindow.offsetValue,
    offsetUnit: rule.sendWindow.offsetUnit,
    timeToSend: rule.sendWindow.sendTimeOptionId || undefined,
  },
  promptInstructions: rule.promptInstructions?.trim()
    ? rule.promptInstructions
    : undefined,
  manualEmailEnabled: Boolean(rule.manualEmailEnabled),
  manualEmailSubject:
    rule.manualEmailEnabled && rule.manualEmailSubject?.trim()
      ? rule.manualEmailSubject
      : undefined,
  manualEmailBody:
    rule.manualEmailEnabled && rule.manualEmailBody?.trim()
      ? rule.manualEmailBody
      : undefined,
});

const hasReachOutRuleChanged = (
  remote: ReachOutCharacterRuleApi,
  local: PremiumCharacterRule
): boolean => {
  if (remote.reachoutCharacterId !== local.reachoutCharacterId) return true;
  const remoteTriggers = remote.triggerCharacterIds ?? [];
  if (remoteTriggers.length !== local.triggerCharacterIds.length) return true;
  for (let index = 0; index < remoteTriggers.length; index += 1) {
    if (remoteTriggers[index] !== local.triggerCharacterIds[index]) return true;
  }
  if (remote.minimumTokens !== local.minimumTokens) return true;
  if ((remote.triggerAfterAnyCount ?? 0) !== (local.triggerAfterAnyCount ?? 0))
    return true;
  if ((remote.resendCooldownDays ?? 0) !== local.resendCooldownDays) return true;
  const remoteWindow = remote.sendWindow ?? {
    offsetValue: 0,
    offsetUnit: "days",
    timeToSend: DEFAULT_SEND_TIME_VALUE,
  };
  if (remoteWindow.offsetValue !== local.sendWindow.offsetValue) return true;
  if (remoteWindow.offsetUnit !== local.sendWindow.offsetUnit) return true;
  const remoteTime = remoteWindow.timeToSend ?? DEFAULT_SEND_TIME_VALUE;
  const localTime =
    local.sendWindow.sendTimeOptionId ?? DEFAULT_SEND_TIME_VALUE;
  if (remoteTime !== localTime) return true;
  if ((remote.promptInstructions ?? "") !== (local.promptInstructions ?? "")) {
    return true;
  }
  if ((remote.reachoutCharacterTier ?? "") !== (local.reachoutCharacterTier ?? "")) {
    return true;
  }
  if (Boolean(remote.manualEmailEnabled) !== Boolean(local.manualEmailEnabled)) {
    return true;
  }
  if ((remote.manualEmailSubject ?? "") !== (local.manualEmailSubject ?? "")) {
    return true;
  }
  if ((remote.manualEmailBody ?? "") !== (local.manualEmailBody ?? "")) {
    return true;
  }
  return false;
};

const CHARACTER_LIBRARY_REQUESTED = "characterLibrary/requested";
const CHARACTER_LIBRARY_RECEIVED = "characterLibrary/received";
const CHARACTER_LIBRARY_FAILED = "characterLibrary/failed";

const CAMPAIGNS_REQUESTED = "campaigns/requested";
const CAMPAIGNS_RECEIVED = "campaigns/received";
const CAMPAIGNS_FAILED = "campaigns/failed";
const CAMPAIGN_CREATED = "campaigns/created";
const CAMPAIGN_SET_ACTIVE = "campaigns/setActive";
const CAMPAIGN_SELECTED = "campaigns/selected";
const CAMPAIGN_UPDATED = "campaigns/updated";
const CAMPAIGN_REMOVED = "campaigns/removed";
const CAMPAIGN_DATA_REPLACED = "campaigns/dataReplaced";

const initialCharacterLibraryState: CharacterLibraryState = {
  items: [],
  status: "idle",
  error: undefined,
};

const characterLibraryReducer = (
  state: CharacterLibraryState = initialCharacterLibraryState,
  action: AnyAction
): CharacterLibraryState => {
  switch (action.type) {
    case CHARACTER_LIBRARY_REQUESTED:
      return { ...state, status: "loading", error: undefined };
    case CHARACTER_LIBRARY_RECEIVED:
      return { items: action.payload, status: "succeeded", error: undefined };
    case CHARACTER_LIBRARY_FAILED:
      return { ...state, status: "failed", error: action.payload };
    default:
      return state;
  }
};

const initialCampaignsState: CampaignsState = {
  items: [],
  status: "idle",
  error: undefined,
  activeCampaignId: undefined,
  selectedCampaignId: undefined,
};

const campaignsReducer = (
  state: CampaignsState = initialCampaignsState,
  action: AnyAction
): CampaignsState => {
  switch (action.type) {
    case CAMPAIGNS_REQUESTED:
      return { ...state, status: "loading", error: undefined };
    case CAMPAIGNS_RECEIVED: {
      const incomingItems: ManagedEmailCampaign[] = action.payload.items;
      const incomingActiveId: string | undefined = action.payload.activeCampaignId;
      const currentSelected = state.selectedCampaignId;
      const selectedStillExists = currentSelected
        ? incomingItems.some((campaign) => campaign.id === currentSelected)
        : false;
      const fallbackSelected =
        incomingActiveId ?? incomingItems[0]?.id ?? undefined;
      const nextSelected = selectedStillExists
        ? currentSelected
        : fallbackSelected;
      return {
        ...state,
        status: "succeeded",
        items: incomingItems,
        activeCampaignId: incomingActiveId,
        selectedCampaignId: nextSelected,
        error: undefined,
      };
    }
    case CAMPAIGNS_FAILED:
      return { ...state, status: "failed", error: action.payload };
    case CAMPAIGN_CREATED:
      return {
        ...state,
        items: [...state.items, action.payload],
        selectedCampaignId: action.payload.id,
      };
    case CAMPAIGN_SET_ACTIVE: {
      const nextActiveId: string | undefined = action.payload;
      return {
        ...state,
        activeCampaignId: nextActiveId,
        items: state.items.map((campaign) => {
          if (nextActiveId) {
            if (campaign.id === nextActiveId) {
              return { ...campaign, status: "active" as const };
            }
            if (campaign.status === "active") {
              return { ...campaign, status: "paused" as const };
            }
            return campaign;
          }
          // No campaign should be active
          if (campaign.status === "active") {
            return { ...campaign, status: "paused" as const };
          }
          return campaign;
        }),
      };
    }
    case CAMPAIGN_SELECTED:
      return { ...state, selectedCampaignId: action.payload };
    case CAMPAIGN_UPDATED:
      return {
        ...state,
        items: state.items.map((campaign) =>
          campaign.id === action.payload.id ? action.payload : campaign
        ),
      };
    case CAMPAIGN_DATA_REPLACED:
      return {
        ...state,
        items: state.items.map((campaign) =>
          campaign.id === action.payload.id
            ? { ...campaign, data: action.payload.data }
            : campaign
        ),
      };
    case CAMPAIGN_REMOVED: {
      const remaining = state.items.filter(
        (campaign) => campaign.id !== action.payload
      );
      const removedSelected = state.selectedCampaignId === action.payload;
      const removedActive = state.activeCampaignId === action.payload;
      const nextActiveId = removedActive
        ? remaining.find((campaign) => campaign.status === "active")?.id
        : state.activeCampaignId;
      const nextSelectedId = removedSelected
        ? nextActiveId ?? remaining[0]?.id
        : state.selectedCampaignId;
      return {
        ...state,
        items: remaining,
        activeCampaignId: nextActiveId,
        selectedCampaignId: nextSelectedId,
      };
    }
    default:
      return state;
  }
};

const createEmailCampaignStore = () => {
  const rootReducer = combineReducers({
    characterLibrary: characterLibraryReducer,
    campaigns: campaignsReducer,
  });
  return createStore(rootReducer, {
    characterLibrary: initialCharacterLibraryState,
    campaigns: initialCampaignsState,
  });
};

interface CharacterLibraryProviderProps {
  children: React.ReactNode;
}

export const CharacterLibraryProvider: React.FC<
  CharacterLibraryProviderProps
> = ({ children }) => {
  const store = useMemo(() => createEmailCampaignStore(), []);

  return <Provider store={store}>{children}</Provider>;
};

export const useCharacterLibrary = () => {
  const dispatch = useDispatch();
  const { items, status, error } = useSelector(
    (state: RootState) => state.characterLibrary
  );

  const refresh = useCallback(async () => {
    dispatch({ type: CHARACTER_LIBRARY_REQUESTED });
    try {
      let apiCharacters: Character[] = [];
      try {
        const popularResponse: any = await getPopularCharacters();
        apiCharacters = extractCharacterPayload(popularResponse);
      } catch (popularError) {
        console.warn("Failed to load popular characters, falling back.", popularError);
      }

      if (!apiCharacters.length) {
        const fallbackResponse: any = await getCharacters();
        apiCharacters = extractCharacterPayload(fallbackResponse);
      }

      const mapped = apiCharacters.map(mapToCampaignCharacter);
      dispatch({ type: CHARACTER_LIBRARY_RECEIVED, payload: mapped });
    } catch (fetchError) {
      console.error("Failed to load characters", fetchError);
      dispatch({
        type: CHARACTER_LIBRARY_FAILED,
        payload:
          fetchError instanceof Error
            ? fetchError.message
            : "Failed to load characters",
      });
    }
  }, [dispatch, status]);

  useEffect(() => {
    if (status === "idle") {
      refresh();
    }
  }, [status, refresh]);

  return {
    characters: items,
    status,
    error,
    refresh,
  };
};

export const useCampaigns = () => {
  const dispatch = useDispatch();
  const { items, status, error, activeCampaignId, selectedCampaignId } =
    useSelector((state: RootState) => state.campaigns);

  const refresh = useCallback(async () => {
    dispatch({ type: CAMPAIGNS_REQUESTED });
    try {
      const summaries = await fetchCampaignSummaries();
      const detailedCampaigns = await Promise.all(
        summaries.map(async (summary) => {
          try {
            const detail = await fetchCampaignById(summary._id);
            return mapCampaignApiToManaged(detail);
          } catch (detailError) {
            console.error(
              `Failed to load campaign ${summary._id}`,
              detailError
            );
            return null;
          }
        })
      );
      const mapped = detailedCampaigns.filter(
        (campaign): campaign is ManagedEmailCampaign => campaign !== null
      );
      const derivedActiveId =
        summaries.find((campaign) => campaign.active)?._id ?? mapped[0]?.id;
      dispatch({
        type: CAMPAIGNS_RECEIVED,
        payload: {
          items: mapped,
          activeCampaignId: derivedActiveId,
        },
      });
    } catch (fetchError) {
      console.error("Failed to load campaigns", fetchError);
      dispatch({
        type: CAMPAIGNS_FAILED,
        payload:
          fetchError instanceof Error
            ? fetchError.message
            : "Failed to load campaigns",
      });
    }
  }, [dispatch]);

  const createCampaign = useCallback(
    async (name: string) => {
      const response = await createCampaignApi(name);
      const mapped = mapCampaignApiToManaged(response);
      dispatch({ type: CAMPAIGN_CREATED, payload: mapped });
      return mapped;
    },
    [dispatch]
  );

  const duplicateCampaign = useCallback(
    async (
      sourceCampaignId: string,
      payload: {
        name: string;
        sections: {
          sequence: boolean;
          characterSelection: boolean;
          replyTiming: boolean;
          noChatIntro: boolean;
          premiumReachouts: boolean;
        };
      }
    ) => {
      const response = await duplicateCampaignApi(sourceCampaignId, payload);
      const mapped = mapCampaignApiToManaged(response);
      dispatch({ type: CAMPAIGN_CREATED, payload: mapped });
      return mapped;
    },
    [dispatch]
  );

  const selectCampaign = useCallback(
    (campaignId: string) => {
      if (!campaignId) return;
      dispatch({ type: CAMPAIGN_SELECTED, payload: campaignId });
    },
    [dispatch]
  );

  const updateCampaignData = useCallback(
    (
      campaignId: string,
      updater: (current: EmailCampaignState) => EmailCampaignState
    ) => {
      const campaign = items.find((item) => item.id === campaignId);
      if (!campaign) return;
      const nextState = updater(cloneCampaignState(campaign.data));
      dispatch({
        type: CAMPAIGN_DATA_REPLACED,
        payload: { id: campaignId, data: nextState },
      });
    },
    [dispatch, items]
  );

  const reloadCampaign = useCallback(
    async (campaignId: string) => {
      const detail = await fetchCampaignById(campaignId);
      const mapped = mapCampaignApiToManaged(detail);
      dispatch({ type: CAMPAIGN_UPDATED, payload: mapped });
      if (mapped.status === "active") {
        dispatch({ type: CAMPAIGN_SET_ACTIVE, payload: mapped.id });
      }
      return mapped;
    },
    [dispatch]
  );

  const saveReengagementSequence = useCallback(
    async (campaignId: string, sequence: SequenceStep[]) => {
      const detail = await fetchCampaignById(campaignId);
      const remoteSteps = detail.reengagementEmailSequence ?? [];
      const remoteMap = new Map(
        remoteSteps.map((step) => [step._id ?? (step as any).id, step])
      );
      const localMap = new Map(sequence.map((step) => [step.id, step]));

      const toDelete = remoteSteps.filter(
        (step) => !localMap.has(step._id ?? (step as any).id)
      );
      const toMutate = sequence.filter(
        (step) => step.id && remoteMap.has(step.id)
      );
      const toCreate = sequence.filter(
        (step) => !step.id || !remoteMap.has(step.id)
      );

      for (const step of toDelete) {
        const stepId = step._id ?? (step as any).id;
        if (!stepId) continue;
        await deleteReengagementSequenceStep(campaignId, stepId);
      }

      for (const step of toMutate) {
        const remote = remoteMap.get(step.id);
        if (!remote) continue;
        if (!hasSequenceStepChanged(remote, step)) continue;
        await updateReengagementSequenceStep(
          campaignId,
          step.id,
          mapSequenceStepToRequestBody(step)
        );
      }

      for (const step of toCreate) {
        await addReengagementSequenceStep(
          campaignId,
          mapSequenceStepToRequestBody(step)
        );
      }

      return reloadCampaign(campaignId);
    },
    [reloadCampaign]
  );

  const saveReengagementCharacterSelection = useCallback(
    async (campaignId: string, settings: EmailCampaignState["settings"]) => {
      await updateReengagementCharacterSelection(campaignId, {
        tokenThreshold: settings.tokenThreshold,
        characterStrategy: mapCharacterStrategyToApi(settings.characterStrategy),
        default_fallback_character_id: settings.defaultCharacterId,
      });
      return reloadCampaign(campaignId);
    },
    [reloadCampaign]
  );

  const saveCharacterReplyTiming = useCallback(
    async (
      campaignId: string,
      threadFollowUp: EmailCampaignState["settings"]["threadFollowUp"]
    ) => {
      await updateCharacterReplyTiming(campaignId, {
        dynamicRange: threadFollowUp.dynamicRange,
      });
      return reloadCampaign(campaignId);
    },
    [reloadCampaign]
  );

  const saveNoChatConfiguration = useCallback(
    async (
      campaignId: string,
      template: EmailCampaignState["promptLibrary"]["noChat"]
    ) => {
      const detail = await fetchCampaignById(campaignId);
      const remoteScenarios =
        detail.noChatIntro?.scenarios ??
        detail.noChatIntro?.triggerScenarios ??
        [];
      const remoteMap = new Map(remoteScenarios.map((scenario) => [scenario._id, scenario]));
      const localScenarios = template.triggerScenarios ?? [];

      const schedulePayload = mapSendScheduleToApi(template.schedule);
      if (schedulePayload) {
        await updateNoChatSchedule(campaignId, schedulePayload);
      }

      const toDelete = remoteScenarios.filter(
        (scenario) => !localScenarios.some((local) => local.id === scenario._id)
      );
      const toMutate = localScenarios.filter((scenario) => remoteMap.has(scenario.id));
      const toCreate = localScenarios.filter(
        (scenario) => !remoteMap.has(scenario.id)
      );

      for (const scenario of toDelete) {
        await deleteNoChatScenario(campaignId, scenario._id);
      }

      for (const scenario of toMutate) {
        const remote = remoteMap.get(scenario.id);
        if (!remote) continue;
        if (!hasNoChatScenarioChanged(remote, scenario)) continue;
        await updateNoChatScenario(
          campaignId,
          scenario.id,
          mapNoChatScenarioToRequest(scenario)
        );
      }

      for (const scenario of toCreate) {
        await addNoChatScenario(campaignId, mapNoChatScenarioToRequest(scenario));
      }

      return reloadCampaign(campaignId);
    },
    [reloadCampaign]
  );

  const saveReachOutRules = useCallback(
    async (campaignId: string, rules: PremiumCharacterRule[]) => {
      const detail = await fetchCampaignById(campaignId);
      const remoteRules = detail.reachOutCharacterRules?.characterRules ?? [];
      const remoteMap = new Map(remoteRules.map((rule) => [rule.id, rule]));

      const toDelete = remoteRules.filter(
        (rule) => !rules.some((local) => local.id === rule.id)
      );
      const toMutate = rules.filter((rule) => remoteMap.has(rule.id));
      const toCreate = rules.filter((rule) => !remoteMap.has(rule.id));

      for (const rule of toDelete) {
        await deleteReachOutRule(campaignId, rule._id);
      }

      for (const rule of toMutate) {
        const remote = remoteMap.get(rule.id);
        if (!remote) continue;
        if (!hasReachOutRuleChanged(remote, rule)) continue;
        await updateReachOutRule(
          campaignId,
          remote._id,
          mapReachOutRuleToRequest(rule)
        );
      }

      for (const rule of toCreate) {
        await addReachOutRule(campaignId, mapReachOutRuleToRequest(rule));
      }

      return reloadCampaign(campaignId);
    },
    [reloadCampaign]
  );

  const activateCampaign = useCallback(
    async (campaignId: string) => {
      const response = await activateCampaignApi(campaignId);
      const mapped = mapCampaignApiToManaged(response);
      dispatch({ type: CAMPAIGN_UPDATED, payload: mapped });
      dispatch({ type: CAMPAIGN_SET_ACTIVE, payload: mapped.id });
      return mapped;
    },
    [dispatch]
  );

  const deactivateCampaign = useCallback(
    async (campaignId: string) => {
      const response = await deactivateCampaignApi(campaignId);
      const mapped = mapCampaignApiToManaged(response);
      dispatch({ type: CAMPAIGN_UPDATED, payload: mapped });
      dispatch({ type: CAMPAIGN_SET_ACTIVE, payload: undefined });
      return mapped;
    },
    [dispatch]
  );

  const deleteCampaign = useCallback(
    async (campaignId: string) => {
      await deleteCampaignApi(campaignId);
      dispatch({ type: CAMPAIGN_REMOVED, payload: campaignId });
    },
    [dispatch]
  );

  useEffect(() => {
    if (status === "idle") {
      refresh();
    }
  }, [status, refresh]);

  return {
    campaigns: items,
    status,
    error,
    activeCampaignId,
    selectedCampaignId,
    refresh,
    createCampaign,
    duplicateCampaign,
    selectCampaign,
    updateCampaignData,
    reloadCampaign,
    saveReengagementSequence,
    saveReengagementCharacterSelection,
    saveCharacterReplyTiming,
    saveNoChatConfiguration,
    saveReachOutRules,
    activateCampaign,
    deactivateCampaign,
    deleteCampaign,
  };
};
